import React, { useEffect, useState, useRef, ReactNode } from 'react'
import { SelectInstance } from 'react-select'; // Import the correct type
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import Form from 'react-bootstrap/Form';
import { useForm, Controller, useFormContext, useFieldArray } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';

import { getBranch } from '../../store/user/selectors'
import { gustoApi } from '../../resources/hooks/api/gustoApiHook'
import { SELECT_CUSTOM_STYLE, URLS } from '../../resources/constants'
import { getGustoSession, getSettings } from '../../store/settings/selectors'
import { useSettingsApi } from '../../resources/hooks/api/useSettingsApiHook'
import { useChartAccountApi } from '../../resources/hooks/api/chartAccountApiHook';
import { GustoCOAClassificationFormI } from '../../resources/form-props';
import { GustoCOAValidate } from '../../resources/form-validator';
import { calculateMaxHeightContent } from '../../resources/functions';
import SelectCustomOption from '../../components/SelectCustomOption';

interface Props { }

const ChartOfAccounts: React.FC<Props> = () => {

    const navigate = useNavigate();
    
    const SettingsData = useSelector(getSettings);
    const isMappingLocked = SettingsData?.gusto_third_party_step_completed >= 1;

    const { gustoFetchEarningTypes, gustoPostEarningClassification } = gustoApi();
    const branchData = useSelector(getBranch);
    const { fetchAccountSettingsData } = useSettingsApi();
    const { glAccountsList } = useChartAccountApi();

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [items, setItems] = useState<any>([])
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);
    const [selectCustomStyle, setSlectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#d1ffbd', '#dfe34d'));
    const selectRefs = useRef<SelectInstance<any, false, any>[]>([]);

    const { register, trigger, control, handleSubmit, reset, setValue, getValues, setError, clearErrors, watch, formState: { errors } } = useForm<GustoCOAClassificationFormI>({
        defaultValues: {
            coa: []
        },
        resolver: yupResolver(GustoCOAValidate)
    });
    const { fields, append: formAppend, remove: formRemove } = useFieldArray({
        control,
        name: "coa"
    });


    const fetchEarningTypes = () => {
        setIsLoading(true)
        gustoFetchEarningTypes({ branch_id: +branchData['id'] }, (msg: string, resp: any) => {
            setIsLoading(false)
            setItems(resp.data.data || [])
        }, (msg: string, resp: any) => {
            setIsLoading(false)
        })
    }

    useEffect(() => {
        items.map((record: any, index: number) => {
            formAppend({
                formIndex: index,
                name: record['name'],
                field_id: record['id'],
                account_id: (record['account_id'] && record['account_name']) ? {
                    label: record['account_name'],
                    value: record['account_id']
                } : null,
            })
        })
    }, [items])

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(131));
    })

    useEffect(() => {
        fetchEarningTypes();
    }, [])

    const fechAccountList = (searchText: string = ''): Promise<any[]> => {
        return new Promise<any>((resolve, reject) => {
            const params: any = {
                "page": 1,
                "per_page": 10,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }

            glAccountsList(params, (message: string, resp: any) => {
                const data = resp.data.data.result.map((e: any) => ({
                    label: e['name'] + `(${e['account']})`,
                    value: e['id']
                }));
                return resolve(data);
                //console.log(data);
                // setGlAccount(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve([]);
            })
        })
    }

    const filterAccountList = async (inputValue: string, item: any) => {
        const data = await fechAccountList(inputValue);
        return data;
    };

    /*const fetchDefaultOptions = () => {
        Object.keys(QB_CLASSIFICATION).map((key: string) => {
            fechAccountList('', key as ClassificationType).then((data: any) => {
                setDropdownOptions((prev: any) => ({
                    ...prev,
                    [key]: data
                }))
            })
        })
    }*/

    const handleSelectChange = (index: number, e: any) => {
        setTimeout(() => {
            let nextIndex = index + 1;
            while (nextIndex < selectRefs.current.length) {
                const nextSelect = selectRefs.current[nextIndex];
                if (nextSelect && !nextSelect.props.isDisabled) {
                    nextSelect.focus();
                    break;
                }
                nextIndex++;
            }
        }, 0);
    };

    // Watch the entire `coa` array
    const coaValues = watch('coa');

    useEffect(() => {
        _handleDuplicateValidation();
    }, [JSON.stringify(coaValues), setError, clearErrors]);

    const _handleDuplicateValidation = (): boolean => {
        const selectedAccountIds = new Set();
        let hasError = false;

        // Iterate through the `coa` array to check for duplicates
        coaValues?.forEach((item, index) => {
            if (item?.account_id?.value) {
                if (selectedAccountIds.has(item.account_id.value)) {
                    // Set an error if a duplicate is found
                    setError(`coa.${index}.account_id`, {
                        type: 'manual',
                        message: 'Duplicate account selected',
                    });
                    hasError = true;
                } else {
                    // Clear the error if no duplicate is found
                    clearErrors(`coa.${index}.account_id`);
                    selectedAccountIds.add(item.account_id.value);
                }
            }
        });

        return hasError;
    }

    const processData = (data: any) => {
        const hasError = _handleDuplicateValidation();
        if (hasError) {
            toast.error("Duplicate account selected");
            return;
        }
        //console.log(data);
        const toRemoveItems: number[] = [];

        const records = data['coa']
            .map((d: any, index: number) => ({
                account_id: d.account_id ? +d.account_id.value : null,
                master_gusto_field_id: d.field_id,
            }))

        postData(records, toRemoveItems);

        // console.log('COA data', data['coa']);

        /*const records = data['coa']
            .map((d: any, index: number) => {
                // if (d['checked']) {
                toRemoveItems.push(index);
                return {
                    account_id: +d.account_id.value,
                    acc_num: d.acc_num,
                    // parent_id: d.parent_id,
                    // description: d.description,
                    name: d.name
                    // transaction_at: d.transaction_at
                };
                // } else {
                //     return null;
                // }
            })
            .filter((e: any) => e); // Filter out null values
        postData(records, toRemoveItems);*/
    }

    const postData = (records: any, toRemoveItems: number[]) => {
        if (records.length) {
            setIsLoading(true);
            gustoPostEarningClassification({ records, branch_id: +branchData['id'] }, (message: string, resp: any) => {
                setIsLoading(false);
                toast.success(message)
                navigate(-1);
                // formRemove(toRemoveItems)
                // if (records.length == fields.length) {
                //     navigate(-1);
                // }
                // fetchCAList();
            }, (message: string, resp: any) => {
                setIsLoading(false);
                toast.error(message);
            })
        } else {
            toast.error("Please select at least one record");
        }
    }

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-4 pt-3" id="ux_page_title">
                    <div className="row align-items-center">
                        <div className="col-sm-6 align-items-center d-flex">
                            <h1 className="h3 font-weight-700 mb-0 d-inline-flex">Chart of Accounts mapping</h1>
                        </div>
                    </div>
                </div>

                <div className=" executive-dashboard">
                    <div className="dashboard-top-part">
                        <div className="filter-btn">
                            <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                <div className="loader"></div>
                            </div>
                        </div>
                        <Form>
                            <div
                                className="table-responsive list-table table-scroll"
                                style={{ /*maxHeight: contentMaxHeight*/ }}
                            >
                                <table className="table table-default">
                                    <thead>
                                        <tr className="align-middle">
                                            <th className="text-start">Gusto Field</th>
                                            <th className="text-start">Revrex Chart of Accounts</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            fields.length > 0 ?
                                                fields.map((data: any, index: any) => {
                                                    return (
                                                        <tr className="align-middle h-60" key={data['id']}>
                                                            <td className="text-start">
                                                                <strong>{fields[index].name}</strong>
                                                            </td>
                                                            <td className="text-start">
                                                                <Form.Group className="col-sm-12 d-flex" controlId="formBasicEmail">
                                                                    <Controller
                                                                        control={control}
                                                                        name={`coa.${index}.account_id`}
                                                                        render={({ field: { value, onChange } }) => (
                                                                            <AsyncSelect
                                                                                ref={(el) => (selectRefs.current[index] = el as SelectInstance<any, false, any>)}
                                                                                isClearable={true}
                                                                                placeholder={'Please select GL acc'}
                                                                                defaultOptions
                                                                                loadOptions={(inputValue: string) => filterAccountList(inputValue, fields[index])}
                                                                                onChange={(e: any) => {
                                                                                    onChange(e);
                                                                                    handleSelectChange(index, e);
                                                                                }}
                                                                                className='vp-dropdown'
                                                                                menuPosition='fixed'
                                                                                value={value}
                                                                                defaultValue={value}
                                                                                styles={selectCustomStyle}
                                                                                components={{ Option: SelectCustomOption }}
                                                                                isDisabled={isMappingLocked}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Form.Group>
                                                                {
                                                                    errors && errors.coa && errors.coa[index] && errors.coa[index]?.account_id && errors.coa[index]?.account_id?.message && (
                                                                        <Form.Text className="text-danger d-flex">
                                                                            <strong>
                                                                                {errors.coa[index]?.account_id?.message as ReactNode || ''}
                                                                            </strong>
                                                                        </Form.Text>
                                                                    ) || ''
                                                                }
                                                            </td>
                                                        </tr>
                                                    );
                                                }) :
                                                <tr>
                                                    <td colSpan={7} className='text-center'>No data found</td>
                                                </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>
                            <div className='text-end mt-3'>
                                {
                                    fields.length > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleSubmit(processData)}
                                            disabled={isMappingLocked}
                                        >
                                            Save Mapping
                                        </button>
                                    ) || <></>
                                }
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ChartOfAccounts
